import { computed, onScopeDispose, watch } from 'vue'

import { useRoute } from 'nuxt/app'

import { useAuthStore } from '@common/auth-layer/store/auth.store'

import { useSentry } from '@common/error-monitoring-layer/composables/useSentry'

import { useLocationStore } from '@common/location-layer/store/location.store'

import { runSupportChatEmbedWatchCycle } from './supportChatEmbedWatchCycle'

import { useSupportChatWidgetQueryStore } from '../stores/support-chat-widget.query-store'

import { isUsableChatWidgetId } from '../utils/support-chat-widget-id.utils'

import { resolveSupportChatPermissionKey } from '../utils/resolveSupportChatPermissionKey'

import { clearSupportChatWidgetMount } from '../utils/supportChatWidgetDom'

/**
 * Injects the portal-configured third-party support chat (LiveChat) when the user is logged in,
 * `widgetId` is set, and `chatWidgetSettings.permissions` allows the current route surface.
 * Client-only; safe to call from `CPRootContainer` during SSR (no-ops until hydrate).
 */
export function useCPSupportChatWidget(): void {
  if (import.meta.server) return

  const route = useRoute()
  const authStore = useAuthStore()
  const locationStore = useLocationStore()
  const queryStore = useSupportChatWidgetQueryStore()
  const { captureException } = useSentry()
  let loadGeneration = 0
  const currentGeneration = () => loadGeneration
  let chatWidgetWatchRun: AbortController | null = null

  const embedFetchEnabled = computed(() => {
    if (!authStore.isLoggedIn) return false
    const locationId = locationStore.locationId
    const widgetId = locationStore.chatWidgetSettings.widgetId
    if (!locationId || !isUsableChatWidgetId(widgetId)) return false
    const surface = resolveSupportChatPermissionKey({ path: route.path, name: route.name })
    if (!surface) return false
    return Boolean(locationStore.chatWidgetSettings.permissions[surface])
  })

  const embedQuery = queryStore.fetchSupportChatEmbedQuery(
    () => locationStore.locationId,
    () => locationStore.chatWidgetSettings.widgetId,
    embedFetchEnabled
  )

  onScopeDispose(() => {
    loadGeneration += 1_000_000
    chatWidgetWatchRun?.abort()
    chatWidgetWatchRun = null
    clearSupportChatWidgetMount()
  })

  watch(
    [
      () => route.fullPath,
      () => route.path,
      () => route.name,
      () => authStore.isLoggedIn,
      () => locationStore.locationId,
      () => locationStore.chatWidgetSettings.widgetId,
      () => locationStore.chatWidgetSettings.permissions.communities,
      () => locationStore.chatWidgetSettings.permissions.membership,
      () => locationStore.chatWidgetSettings.permissions.home,
      () => locationStore.chatWidgetSettings.permissions.affiliate,
      () => embedFetchEnabled.value,
      () => embedQuery.status.value,
      () => embedQuery.fetchStatus.value,
      () => embedQuery.data.value,
      () => embedQuery.isError.value,
      () => embedQuery.error.value,
    ],
    async () => {
      chatWidgetWatchRun?.abort()
      chatWidgetWatchRun = new AbortController()
      const signal = chatWidgetWatchRun.signal
      const generation = ++loadGeneration

      await runSupportChatEmbedWatchCycle({
        authStore,
        locationStore,
        embedFetchEnabled,
        embedQuery,
        queryStore,
        captureException,
        currentGeneration,
        signal,
        generation,
      })
    },
    { immediate: true }
  )
}
